/**
 * GalleryOverviewPage - Dashboard overview for a gallery.
 *
 * Provides:
 * - Hero CTA for starting review workflow
 * - Status summary cards
 * - Quick image preview grid (non-virtualized, limited)
 * - Recent activity
 *
 * This is the landing page when navigating to a gallery with V2 enabled.
 *
 * @see /src/features/gallery/layouts/GalleryLayout.tsx - Parent layout
 * @see /src/features/gallery/components/shared/HeroCTA.tsx - Primary CTA component
 */

import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  CheckCircle,
  Clock,
  XCircle,
  ImageIcon,
  ArrowRight,
  Star,
  Briefcase,
  Crop,
  RotateCcw,
  Type,
  Loader2,
} from 'lucide-react'
import { useTextDetection } from '../hooks/useTextDetection'
import { useImageCrop } from '../hooks/useImageCrop'
import { isCloudflareImageUrl } from '@/lib/cloudflareImages'

import { useGalleryDetail } from '../context/GalleryDetailContext'
import { HeroCTA } from '../components/shared/HeroCTA'
import { GalleryStatusBadge } from '../components/shared/GalleryStatusBadge'
import { LightboxModal } from '../components/shared/LightboxModal'
import { ServiceSelector } from '../components/services/ServiceSelector'
import { cn } from '@/lib/utils'

/**
 * Stats card component.
 */
interface StatsCardProps {
  title: string
  value: number | string
  icon: React.ReactNode
  variant?: 'default' | 'success' | 'warning' | 'destructive' | 'info'
  subtitle?: string
}

function StatsCard({ title, value, icon, variant = 'default', subtitle }: StatsCardProps) {
  const variantStyles = {
    default: 'bg-muted/50',
    success: 'bg-success/10',
    warning: 'bg-warning/10',
    destructive: 'bg-destructive/10',
    info: 'bg-info/10',
  }

  const iconStyles = {
    default: 'text-muted-foreground',
    success: 'text-success',
    warning: 'text-warning',
    destructive: 'text-destructive',
    info: 'text-info',
  }

  // Card-level styles for prominent variants
  const cardStyles = {
    default: '',
    success: '',
    warning: 'ring-2 ring-warning/40 shadow-lg shadow-warning/10 bg-warning/5',
    destructive: '',
    info: '',
  }

  return (
    <Card className={cn('transition-all', cardStyles[variant])}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className={cn(
              'text-2xl font-semibold mt-1',
              variant === 'warning' && 'text-warning'
            )}>{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          <div className={cn('p-2 rounded-lg', variantStyles[variant])}>
            <div className={iconStyles[variant]}>{icon}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Image preview thumbnail with text detection and crop support.
 */
interface PreviewThumbnailProps {
  image: {
    original_id: number | null
    image_url: string | null
    review_status?: string | null
    is_cover?: boolean | null
    image_type?: string | null
    /** The franchise_image_jobs.id for job images (needed for crop operations) */
    franchise_image_job_id?: number | null
  }
  onClick?: () => void
  /** Makes this thumbnail span 2x2 in the grid */
  isLarge?: boolean
}

function PreviewThumbnail({ image, onClick, isLarge = false }: PreviewThumbnailProps) {
  const [hasDetectedText, setHasDetectedText] = useState<boolean | null>(null)
  const { detectText, isDetecting } = useTextDetection({ scanBottomOnly: true })

  // Check if this is a Cloudflare image (crop feature only works for those)
  const canCrop = image.image_url && isCloudflareImageUrl(image.image_url)

  // Determine the correct ID for crop operations
  // For job images, use franchise_image_job_id; for gallery photos, use original_id
  const cropImageId = image.image_type === 'job_image'
    ? image.franchise_image_job_id
    : image.original_id

  // Crop state with persistence - saves to Supabase franchise_image_jobs table
  const { isCropped, toggleCrop, isUpdating: isCropUpdating } = useImageCrop({
    imageId: cropImageId ?? 0, // Fallback to 0 if null (won't be called if canCrop is false)
    imageUrl: image.image_url || '',
  })

  // Use image URL directly - it's already the correct variant in the database
  const currentImageUrl = image.image_url

  // Auto-detect text for job images
  useEffect(() => {
    if (image.image_url && canCrop && hasDetectedText === null) {
      detectText(image.image_url).then(result => {
        setHasDetectedText(result.hasText)
      }).catch(() => {
        setHasDetectedText(false)
      })
    }
  }, [image.image_url, canCrop, detectText, hasDetectedText])

  // Status badge styles
  const statusBadgeStyles: Record<string, string> = {
    approved: 'bg-success/90 text-white',
    rejected: 'bg-destructive/90 text-white',
    pending: 'bg-warning/90 text-warning-foreground',
    under_review: 'bg-info/90 text-white',
  }

  const statusLabel: Record<string, string> = {
    approved: 'Approved',
    rejected: 'Rejected',
    pending: 'Pending',
    under_review: 'Review',
  }

  return (
    <div
      className={cn(
        'relative rounded-lg overflow-hidden bg-muted cursor-pointer group transition-all duration-200 hover:shadow-xl hover:z-10 ring-0 hover:ring-2 hover:ring-primary/30',
        isLarge
          ? 'col-span-2 row-span-2 hover:scale-[1.02]'
          : 'aspect-square hover:scale-105'
      )}
      onClick={onClick}
    >
      <img
        src={currentImageUrl ?? ''}
        alt=""
        className={cn(
          'w-full h-full object-cover transition-transform duration-300 group-hover:scale-110',
          isLarge && 'aspect-square'
        )}
        loading="lazy"
      />
      {/* Status indicator - pill badge instead of dot */}
      {image.review_status && (
        <Badge
          className={cn(
            'absolute top-2 left-2 text-xs font-medium shadow-sm',
            statusBadgeStyles[image.review_status] || 'bg-muted text-muted-foreground'
          )}
        >
          {statusLabel[image.review_status] || image.review_status}
        </Badge>
      )}

      {/* Text detection / crop indicators - Top right area */}
      <div className="absolute top-2 right-2 flex items-center gap-1">
        {isDetecting && (
          <Badge variant="secondary" className="h-5 w-5 p-0 flex items-center justify-center bg-background/80">
            <Loader2 className="h-3 w-3 animate-spin" />
          </Badge>
        )}
        {hasDetectedText === true && !isDetecting && !isCropped && (
          <Badge variant="secondary" className="h-5 w-5 p-0 flex items-center justify-center bg-warning/90 text-warning-foreground">
            <Type className="h-3 w-3" />
          </Badge>
        )}
        {isCropped && (
          <Badge variant="secondary" className="h-5 w-5 p-0 flex items-center justify-center bg-info/90 text-info-foreground">
            <Crop className="h-3 w-3" />
          </Badge>
        )}
        {/* Cover indicator */}
        {image.is_cover && (
          <Badge variant="secondary" className="gap-1 text-xs">
            <Star className="h-3 w-3 fill-current" />
            Cover
          </Badge>
        )}
      </div>

      {/* Crop/Reset button on hover - persists to database */}
      {canCrop && (
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            size="sm"
            variant="secondary"
            className="h-7 text-xs bg-background/90 hover:bg-background"
            disabled={isCropUpdating}
            onClick={(e) => {
              e.stopPropagation()
              toggleCrop()
            }}
          >
            {isCropUpdating ? (
              <>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </>
            ) : isCropped ? (
              <>
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset
              </>
            ) : (
              <>
                <Crop className="h-3 w-3 mr-1" />
                Crop
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}

export default function GalleryOverviewPage() {
  const { id } = useParams<{ id: string }>()
  const basePath = `/media/galleries/${id}`

  const {
    gallery,
    images,
    stats,
    enrichment,
    isLoading,
    openLightbox,
    lightbox,
    closeLightbox,
    navigateLightbox,
    setLightboxIndex,
  } = useGalleryDetail()

  // Get preview images (first 8)
  const previewImages = images.slice(0, 8)
  const hasMoreImages = images.length > 8

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Hero CTA - Primary action */}
      <div className="animate-fade-down">
        <HeroCTA
          href={`${basePath}/review`}
          pendingCount={stats.pending}
          totalCount={stats.total}
        />
      </div>

      {/* Stats Grid - uses CSS stagger-children for sequential entrance */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 stagger-children">
        <div>
          <StatsCard
            title="Total Images"
            value={stats.total}
            icon={<ImageIcon className="h-5 w-5" />}
            subtitle={`${stats.jobImages} from Jobber • ${stats.directPhotos} uploads`}
          />
        </div>

        <div>
          <StatsCard
            title="Approved"
            value={stats.approved}
            icon={<CheckCircle className="h-5 w-5" />}
            variant="success"
            subtitle={stats.jobImages > 0
              ? `${stats.approved} of ${stats.jobImages} job images (${Math.round((stats.approved / stats.jobImages) * 100)}%)`
              : stats.directPhotos > 0 ? 'Direct uploads only' : undefined}
          />
        </div>

        <div>
          <StatsCard
            title="Pending Review"
            value={stats.pending}
            icon={<Clock className="h-5 w-5" />}
            variant="warning"
            subtitle={stats.jobImages > 0
              ? `${stats.pending} of ${stats.jobImages} need review`
              : undefined}
          />
        </div>

        <div>
          <StatsCard
            title="Rejected"
            value={stats.rejected}
            icon={<XCircle className="h-5 w-5" />}
            variant="destructive"
            subtitle={stats.rejected > 0
              ? `${stats.rejected} of ${stats.jobImages} job images`
              : undefined}
          />
        </div>
      </div>

      {/* Quick Info Cards - uses CSS stagger-children */}
      <div className="grid gap-4 md:grid-cols-2 stagger-children">
        {/* Gallery Info */}
        <div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Gallery Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {gallery?.title && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Title</span>
                  <span className="font-medium">{gallery.title}</span>
                </div>
              )}
              {gallery?.franchise_region && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Region</span>
                  <span className="capitalize font-medium">
                    {gallery.franchise_region.replace('_', ' ')}
                  </span>
                </div>
              )}
              {gallery?.job_number && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Job Number</span>
                  <span className="font-medium">#{gallery.job_number}</span>
                </div>
              )}
              {gallery?.review_status && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Status</span>
                  <GalleryStatusBadge status={gallery.review_status} size="sm" />
                </div>
              )}
              {gallery?.created_on && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>{new Date(gallery.created_on).toLocaleDateString()}</span>
                </div>
              )}

              {/* Services - editable via ServiceSelector */}
              <div className="pt-2 border-t">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-muted-foreground">Services</span>
                </div>
                {gallery?.id && (
                  <ServiceSelector galleryId={gallery.id} compact />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Job Info (from enrichment) */}
        <div>
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Job Details</CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`${basePath}/job`} className="gap-1">
                    View All
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {enrichment ? (
                <>
                  {enrichment.job_status && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status</span>
                      <Badge variant="secondary">{enrichment.job_status}</Badge>
                    </div>
                  )}
                  {enrichment.job_total && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total</span>
                      <span className="font-medium">
                        ${Number(enrichment.job_total).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {enrichment.service_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Service Date</span>
                      <span>
                        {new Date(enrichment.service_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {enrichment.service_city && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Location</span>
                      <span>{enrichment.service_city}</span>
                    </div>
                  )}
                </>
              ) : (
                /* Compact empty state - less visual prominence */
                <div className="flex items-center justify-between py-2 px-3 rounded-md bg-muted/30 border border-dashed border-muted-foreground/20">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Briefcase className="h-4 w-4 opacity-60" />
                    <span className="text-sm">No job data linked</span>
                  </div>
                  <Button variant="ghost" size="sm" asChild className="h-7 text-xs">
                    <Link to={`${basePath}/job`}>
                      Link Job
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image Preview Grid */}
      <div className="animate-fade-in animate-delay-200">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Image Preview</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to={`${basePath}/review`} className="gap-1">
                  View All
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {previewImages.length > 0 ? (
              <>
                {/* Asymmetric grid: first image is 2x2, rest are 1x1 */}
                <div className="grid grid-cols-4 grid-rows-2 gap-3 stagger-children">
                  {previewImages.slice(0, 5).map((image, index) => (
                    <PreviewThumbnail
                      key={image.original_id}
                      image={image}
                      onClick={() => openLightbox(index)}
                      isLarge={index === 0}
                    />
                  ))}
                </div>
                {hasMoreImages && (
                  <div className="mt-4 text-center">
                    <Button variant="outline" asChild>
                      <Link to={`${basePath}/review`}>
                        View all {images.length} images
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center py-8 text-center text-muted-foreground">
                <ImageIcon className="h-12 w-12 mb-3 opacity-50" />
                <p className="font-medium">No images in this gallery</p>
                <p className="text-sm">Add images from Jobber or upload directly</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lightbox Modal */}
      {lightbox.isOpen && (
        <LightboxModal
          images={lightbox.images}
          currentIndex={lightbox.currentIndex}
          onClose={closeLightbox}
          onNavigate={navigateLightbox}
          onIndexChange={setLightboxIndex}
        />
      )}
    </div>
  )
}
